<script lang="ts" setup>
import { SEARCH_RESULT_TYPES } from '~/constants/searchResultTypes';
import type { OfferImage } from '~/types/Catalog';
import type { SearchByQueryResponse } from '~/types/Search';

defineEmits(['close-search', 'go-to-search-page']);
const { results } = defineProps<{ results: SearchByQueryResponse }>();

const searchStore = useSearchStore();

onMounted(() => {
  searchStore.getSearchHistory();
});

const hasSearchResults = computed(
  () =>
    !!results.queries.length ||
    !!results.brands.length ||
    !!results.categories.length ||
    !!results.pages.length ||
    !!results.promotions.length ||
    !!results.articles.length ||
    !!results.offers.length,
);

const filteredSearchHistory = computed(() =>
  searchStore.searchHistory.filter((item) => {
    if (!item.name) return false;

    return item.name.toLowerCase().includes(searchStore.searchQuery?.toLowerCase());
  }),
);

function getMainImage(images: OfferImage[]) {
  if (!images.length) return undefined;

  return images.find((image) => image.is_main) || images[0];
}
</script>

<template>
  <section class="search-results">
    <template v-if="filteredSearchHistory.length">
      <div
        v-for="item in filteredSearchHistory"
        :key="item.url"
        class="search-results__history-item"
      >
        <AppLink
          :to="makeUrlRelative(item.url)"
          class="flex items-center gap-1.5"
          @click="$emit('close-search')"
        >
          <SvgoHistory class="text-xl text-black-60 flex-shrink-0" />
          <div v-html="highlightTextOccurrence(item.name, searchStore.searchQuery)" />
        </AppLink>

        <button
          class="text-xl text-black-60 hover:text-black cursor-pointer"
          @click="searchStore.removeFromSearchHistory(item)"
        >
          <SvgoCross />
        </button>
      </div>
    </template>

    <section
      v-if="hasSearchResults"
      @click="$emit('close-search')"
    >
      <section v-if="results.queries.length">
        <h3 class="search-results__group-title">Запросы</h3>

        <SearchResultsListItem
          v-for="query in results.queries"
          :key="query.id"
          :name="query.title"
          :type="SEARCH_RESULT_TYPES.content"
          :url="query.url"
        />
      </section>

      <section v-if="results.brands.length">
        <h3 class="search-results__group-title">Бренды</h3>

        <SearchResultsListItem
          v-for="brand in results.brands"
          :key="brand.slug"
          :image="brand.logo"
          :name="brand.name"
          :type="SEARCH_RESULT_TYPES.brand"
          :url="brand.uri"
        />
      </section>

      <section v-if="results.categories.length">
        <h3 class="search-results__group-title">Категории</h3>

        <SearchResultsListItem
          v-for="category in results.categories"
          :key="category.slug"
          :image="{ url: category.svg_url } as OfferImage"
          :name="category.name"
          :type="SEARCH_RESULT_TYPES.category"
          :url="category.uri"
        />
      </section>

      <section v-if="results.pages.length || results.promotions.length">
        <h3 class="search-results__group-title">Страницы и акции</h3>

        <SearchResultsListItem
          v-for="page in results.pages"
          :key="page.id"
          :name="page.title"
          :type="SEARCH_RESULT_TYPES.content"
          :url="page.url"
        />

        <SearchResultsListItem
          v-for="promotion in results.promotions"
          :key="promotion.id"
          :name="promotion.title"
          :type="SEARCH_RESULT_TYPES.content"
          :url="promotion.url"
        />
      </section>

      <section v-if="results.articles.length">
        <h3 class="search-results__group-title">Статьи</h3>

        <SearchResultsListItem
          v-for="article in results.articles"
          :key="article.id"
          :name="article.title"
          :type="SEARCH_RESULT_TYPES.content"
          :url="article.url"
        />
      </section>

      <section v-if="results.offers.length">
        <h3 class="search-results__group-title">Товары</h3>

        <SearchResultsListItem
          v-for="offer in results.offers.slice(0, 6)"
          :key="offer.id"
          :image="getMainImage(offer.offer_images)"
          :name="offer.product_name"
          :price="offer.price"
          :type="SEARCH_RESULT_TYPES.offer"
          :url="offer.uri"
        />
      </section>

      <a
        class="search-results__bottom-text cursor-pointer"
        @click="$emit('go-to-search-page')"
      >
        Все результаты
      </a>
    </section>

    <AppLink
      v-else-if="searchStore.searchQuery.length"
      class="search-results__bottom-text"
      to="/catalog"
      @click="$emit('close-search')"
    >
      Посмотреть каталог
    </AppLink>

    <section
      v-else
      class="search-results__bottom-text"
    >
      Введите поисковый запрос
    </section>
  </section>
</template>

<style lang="scss" scoped>
.search-results {
  overflow-y: auto;
  width: 100%;
  height: fit-content;
  max-height: 65vh;
  font-size: $text-xs;

  @include screen-sm {
    max-height: 60vh;
    font-size: $text-sm;
  }

  &__history-item {
    display: grid;
    grid-template-columns: 1fr 24px;
    gap: 16px;
    align-items: center;
    overflow: auto;
    height: 40px;
    border-bottom: 1px solid $black-20;
    font-size: 14px;
  }

  &__group-title {
    padding: 16px 0 4px;
    color: $black-60;
    font-weight: $bold;
    font-size: $text-xs;
    text-transform: uppercase;
  }

  &__bottom-text {
    display: block;
    width: 100%;
    padding: 16px 8px 8px;
    font-weight: $bold;
    font-size: $text-xs;
    text-align: center;
  }
}
</style>
